import React, { useCallback, useRef } from 'react'

// The WAI-ARIA radio-group keyboard pattern for the custom role='radio' rows the recovery screens
// draw: a SINGLE tab stop, arrow keys moving between rows and selecting as they go. Shared by D1's
// reason list and D3's method list. D2's checkbox rows are each their own tab stop, so they don't.
export const useRovingRadioGroup = <T>(
  values: readonly T[],
  selected: T | null,
  onSelect: (value: T) => void
) => {
  const rows = useRef<(HTMLDivElement | null)[]>([])

  // With nothing selected the FIRST row holds the tab stop, per the pattern, so the group is
  // always reachable in one Tab.
  const selectedIndex = selected === null ? -1 : values.indexOf(selected)
  const tabStopIndex = selectedIndex === -1 ? 0 : selectedIndex

  const move = useCallback(
    (from: number, delta: number) => {
      // Wraps at both ends, like a native radio group.
      const to = (from + delta + values.length) % values.length
      const value = values[to]
      if (value === undefined) return
      onSelect(value)
      rows.current[to]?.focus()
    },
    [onSelect, values]
  )

  // Props for one row, in render order — spread onto RecoveryChoiceRow, which applies each to the
  // element itself. Do NOT spread onto a bare div: that hides the handlers from static analysis,
  // which then reads the row as click-only (Sonar S1082). onClick lives here so a row expresses
  // "select me" once; it stays a separate handler because a div fires no click for Enter/Space.
  return (value: T, index: number) => ({
    ref: (el: HTMLDivElement | null) => {
      rows.current[index] = el
    },
    tabIndex: index === tabStopIndex ? 0 : -1,
    onClick: () => onSelect(value),
    onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onSelect(value)
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault()
        move(index, 1)
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault()
        move(index, -1)
      }
    }
  })
}

export default useRovingRadioGroup
